/* eslint-disable react-hooks/exhaustive-deps */
// Shim that presents the single real React 17 as if it also had the newer APIs modern
// libraries import from `react`: useId + useInsertionEffect (framer-motion),
// useSyncExternalStore (@tanstack/react-query v5, @azure/msal-react, …) and
// startTransition/useOptimistic/use/cache (react-router v8). It stays a thin namespace
// wrapper over ONE React runtime — not a second copy.
//
// Wiring (vite.config.ts): an enforce:'pre' resolveId plugin plus a resolve.alias rewrite
// the exact bare specifier `react` to this file, so every `import ... from 'react'` lands
// here. Only the exact id `react` is rewritten — `react/index.js` (used here to reach the
// real package without looping), `react-dom`, `react/jsx-runtime`, etc. pass through.
//
// The real React 17 names are re-exported by enumeration, NOT `export * from 'react/index.js'`.
// `export *` looks equivalent and passes isolated build/dev/Vitest checks, but does NOT hold
// up in the running app, so each name is forwarded by hand. The list doubles as documentation
// of exactly which React 17 names the shim forwards.
import * as React from 'react/index.js';

export default React;

export const {
    Children,
    Component,
    Fragment,
    Profiler,
    PureComponent,
    StrictMode,
    Suspense,
    cloneElement,
    createContext,
    createElement,
    createFactory,
    createRef,
    forwardRef,
    isValidElement,
    lazy,
    memo,
    useCallback,
    useContext,
    useDebugValue,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
    version,
    // Required by react/jsx-dev-runtime — it reaches into this object for
    // ReactCurrentDispatcher. Without it, every JSX render crashes at startup.
    __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED,
} = React;

// Counter-based polyfill — React 17 has no useId. Stable across re-renders via the ref;
// not SSR-safe, but this app is client-only.
let counter = 0;

export const useId = (): string => {
    const ref = React.useRef<string | null>(null);
    if (ref.current === null) {
        ref.current = `:r${(counter++).toString(36)}:`;
    }
    return ref.current;
};

// useInsertionEffect doesn't exist in React 17. useLayoutEffect is the conventional
// fallback (same one styled-components / emotion use); fires slightly later than the
// real thing but is fine for framer-motion's style-tag injection.
export const useInsertionEffect = React.useLayoutEffect;

// useSyncExternalStore doesn't exist in React 17. We can't re-export it from the
// `use-sync-external-store` package: its CJS internally does `require('react')`,
// which the vite alias routes back into this module while it's still initializing,
// producing a circular dependency (React === undefined at the shim's eval time).
// So we inline the official client-only shim here, backed by the real React 17 hooks
// from `react/index.js`. This is the same DOM implementation the package ships; the
// SSR branch is dropped since this app is client-only. Consumers (e.g. react-query)
// only need `React.useSyncExternalStore` to exist on the namespace.
const objectIs: (x: unknown, y: unknown) => boolean =
    typeof Object.is === 'function' ? Object.is : (x, y) => (x === y ? x !== 0 || 1 / (x as number) === 1 / (y as number) : x !== x && y !== y);

const checkIfSnapshotChanged = <T>(inst: { value: T; getSnapshot: () => T }): boolean => {
    const latestGetSnapshot = inst.getSnapshot;
    const prevValue = inst.value;
    try {
        return !objectIs(prevValue, latestGetSnapshot());
    } catch {
        return true;
    }
};

export const useSyncExternalStore = <T>(subscribe: (onStoreChange: () => void) => () => void, getSnapshot: () => T): T => {
    const value = getSnapshot();
    const [{ inst }, forceUpdate] = React.useState({ inst: { value, getSnapshot } });

    React.useLayoutEffect(() => {
        inst.value = value;
        inst.getSnapshot = getSnapshot;
        if (checkIfSnapshotChanged(inst)) {
            forceUpdate({ inst });
        }
    }, [subscribe, value, getSnapshot]);

    React.useEffect(() => {
        if (checkIfSnapshotChanged(inst)) {
            forceUpdate({ inst });
        }
        return subscribe(() => {
            if (checkIfSnapshotChanged(inst)) {
                forceUpdate({ inst });
            }
        });
    }, [subscribe]);

    React.useDebugValue(value);
    return value;
};

// --- React 18/19 APIs required by react-router v8 on React 17 -------------------
// react-router v8 does `import * as React from 'react'` and reaches for these on the
// namespace (and named-imports `startTransition`/`useOptimistic`, which Rolldown must be
// able to resolve here or dep pre-bundling fails). React 17 has none of them. The app runs
// the classic `useRoutes`/`BrowserRouter` API, so the data-router paths that actually
// exercise these never render — the polyfills exist to keep the bundle resolvable and to
// degrade gracefully, not to reproduce concurrent semantics.

// React 18. No concurrent scheduler in 17 — run the update synchronously.
export const startTransition = (scope: () => void): void => {
    scope();
};

// React 19. Real useOptimistic layers a transient value over base state and auto-reverts
// when the surrounding transition settles. Without a concurrent scheduler we approximate:
// track a local override, reset it whenever the passthrough value changes. Client-only.
export const useOptimistic = <State, Action>(passthrough: State, reducer?: (state: State, action: Action) => State): [State, (action: Action) => void] => {
    const [optimistic, setOptimistic] = React.useState(passthrough);
    React.useEffect(() => {
        setOptimistic(passthrough);
    }, [passthrough]);
    const addOptimistic = React.useCallback(
        (action: Action) => {
            setOptimistic(prev => (reducer ? reducer(prev, action) : (action as unknown as State)));
        },
        [],
    );
    return [optimistic, addOptimistic];
};

// React 19. Reads a context, or suspends on a thenable by throwing it. Only hit on
// server/RSC code paths this client SPA never renders.
export const use = <T>(usable: React.Context<T> | Promise<T>): T => {   
    if (usable != null && typeof (usable as Promise<T>).then === 'function') {
        throw usable;
    }
    return React.useContext(usable as React.Context<T>);
};

// React 19 server API. No request-scoped memoization on 17 — identity passthrough.
export const cache = <Fn extends (...args: never[]) => unknown>(fn: Fn): Fn => fn;
